import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconPencil, IconPower } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Can, EmptyMessage } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";
import SelectItemsPanel from "./SelectItemsPanel";

function statusColor(status) {
  if (status === 1) return "green";
  if (status === 2) return "red";
  return "gray";
}

export default function InstrumentItemsPanel({
  conceptualRootId,
  conceptualItemId,
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [interrogative, setInterrogative] = useState("");
  const [creating, setCreating] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchItems();
  }, [conceptualRootId, conceptualItemId]);

  function basePath() {
    return `/conceptual-roots/${conceptualRootId}/conceptual-items/${conceptualItemId}`;
  }

  function fetchItems() {
    setLoading(true);
    axios
      .post(`${basePath()}/retrieve-instrument-items/`, {
        status: "entire",
        page: 0,
        page_size: 100,
      })
      .then(({ data }) => {
        const rows = data.response[0]?.data || [];
        setItems(rows);
        setDrafts(
          Object.fromEntries(
            rows.map((row) => [row.id, row.interrogative || ""])
          )
        );
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function onCreate() {
    if (!interrogative.trim()) {
      toast.error(t("validation:required"));
      return;
    }
    setCreating(true);
    axios
      .post(`${basePath()}/instrument-items/`, {
        interrogative: interrogative.trim(),
      })
      .then(() => {
        setCreating(false);
        setInterrogative("");
        setShowCreate(false);
        toast.success(t("created"));
        fetchItems();
      })
      .catch((err) => {
        setCreating(false);
        toast.error(err);
      });
  }

  function toggleActive(itemId) {
    setTogglingId(itemId);
    axios
      .post(`${basePath()}/instrument-items/${itemId}/active-toggle/`)
      .then(() => {
        setTogglingId(null);
        toast.success(t("updated"));
        fetchItems();
      })
      .catch((err) => {
        setTogglingId(null);
        toast.error(err);
      });
  }

  function updateInterrogative(itemId) {
    const value = (drafts[itemId] || "").trim();
    if (!value) {
      toast.error(t("validation:required"));
      return;
    }
    setSavingId(itemId);
    axios
      .put(`${basePath()}/instrument-items/${itemId}/`, {
        interrogative: value,
      })
      .then(() => {
        setSavingId(null);
        setEditingId(null);
        toast.success(t("updated"));
        fetchItems();
      })
      .catch((err) => {
        setSavingId(null);
        toast.error(err);
      });
  }

  function startEdit(item) {
    setEditingId(item.id);
    setDrafts((prev) => ({
      ...prev,
      [item.id]: item.interrogative || "",
    }));
  }

  function cancelEdit(item) {
    setEditingId(null);
    setDrafts((prev) => ({
      ...prev,
      [item.id]: item.interrogative || "",
    }));
  }

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Can permission={PERMISSIONS.CONCEPTUAL_ITEMS_CREATE}>
        {!showCreate ? (
          <Group>
            <Button
              variant="light"
              onClick={() => setShowCreate(true)}
            >
              {t("add_instrument_item")}
            </Button>
          </Group>
        ) : (
          <Card>
            <Group justify="space-between" mb="sm">
              <Title order={4}>
                {t("add_instrument_item")}
              </Title>
              <Button
                size="xs"
                variant="subtle"
                onClick={() => {
                  setShowCreate(false);
                  setInterrogative("");
                }}
              >
                {t("cancel")}
              </Button>
            </Group>
            <Stack gap="sm">
              <TextInput
                label={t("interrogative")}
                value={interrogative}
                onChange={(e) => setInterrogative(e.target.value)}
                required
              />
              <Button loading={creating} onClick={onCreate}>
                {t("create")}
              </Button>
            </Stack>
          </Card>
        )}
      </Can>

      {!items.length && <EmptyMessage />}
      {items.map((item) => {
        const isExpanded = expandedId === item.id;
        return (
          <Card key={item.id}>
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start">
                <Text fw={700} style={{ flex: 1 }}>
                  {item.interrogative}
                </Text>
                <Group gap="xs">
                  <Badge variant="light" color={statusColor(item.status)}>
                    {item.status_formatted || t("status")}
                  </Badge>
                  <Can permission={PERMISSIONS.CONCEPTUAL_ITEMS_EDIT}>
                    <ActionIcon
                      size="sm"
                      variant="light"
                      color={item.status === 1 ? "red" : "green"}
                      title={t("activation_status")}
                      loading={togglingId === item.id}
                      onClick={() => toggleActive(item.id)}
                    >
                      <IconPower size={14} />
                    </ActionIcon>
                    {editingId !== item.id && (
                      <ActionIcon
                        size="sm"
                        variant="light"
                        title={t("edit")}
                        onClick={() => startEdit(item)}
                      >
                        <IconPencil size={14} />
                      </ActionIcon>
                    )}
                  </Can>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() =>
                      setExpandedId(isExpanded ? null : item.id)
                    }
                  >
                    {isExpanded ? "Hide selects" : "Select items"}
                  </Button>
                </Group>
              </Group>

              {editingId === item.id && (
                <Can permission={PERMISSIONS.CONCEPTUAL_ITEMS_EDIT}>
                  <Stack gap="sm">
                    <TextInput
                      label={t("interrogative")}
                      value={drafts[item.id] ?? ""}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [item.id]: e.target.value,
                        }))
                      }
                    />
                    <Group>
                      <Button
                        loading={savingId === item.id}
                        onClick={() => updateInterrogative(item.id)}
                      >
                        {t("save")}
                      </Button>
                      <Button
                        variant="default"
                        onClick={() => cancelEdit(item)}
                      >
                        {t("cancel")}
                      </Button>
                    </Group>
                  </Stack>
                </Can>
              )}

              {isExpanded && (
                <SelectItemsPanel
                  conceptualRootId={conceptualRootId}
                  conceptualItemId={conceptualItemId}
                  instrumentItemId={item.id}
                />
              )}
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}
